import { useCallback, useMemo } from 'react';
import { useSearchParams } from './use-search-params';

type QueryParamValue = string | string[] | number | null | undefined | false;

type QueryParams = Record<string, QueryParamValue>;

export type SetQueryParamsOptions = {
  overwrite?: boolean | undefined;
  replace?: boolean | undefined;
  state?: Record<string, unknown> | null | undefined;
};

const checkFalsy = (value: QueryParamValue) =>
  value === null || typeof value === 'undefined' || value === false;

export const useQueryParams = <T extends QueryParams>() => {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(
    () =>
      [...searchParams].reduce<Record<string, string | string[]>>((acc, [key, value]) => {
        if (acc[key]) {
          const existingValue = acc[key];

          acc[key] =
            typeof existingValue === 'string'
              ? [existingValue, value]
              : existingValue.concat(value);
        } else {
          acc[key] = value;
        }

        return acc;
      }, {}),
    [searchParams],
  ) as unknown as T;

  const setQueryParams = useCallback(
    (q: T, options?: SetQueryParamsOptions) => {
      const { overwrite, ...restOptions } = options || {};
      const newQuery = overwrite ? q : { ...query, ...q };
      const filteredQuery = Object.fromEntries(
        Object.entries(newQuery).filter(([, value]) => !checkFalsy(value)),
      ) as T;

      setSearchParams(filteredQuery, restOptions);
    },
    [query, setSearchParams],
  );

  return {
    query,
    setQueryParams,
  };
};
